import { Sparkle } from "@phosphor-icons/react";
import { useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useApi } from "~/api/provider";
import { DOCUMENT_ROOT } from "~/api/queries";
import type { SculptStreamEvent } from "~/api/types";
import { Button } from "~/foundation/components/Button";
import { Text } from "~/foundation/components/Text";

export interface ComposeCardProps {
  documentId: string;
  /**
   * W7 — what the person typed in the New-document dialog, handed over through session
   * storage so a reload does not re-run the compose. Prefills the box; never auto-runs it.
   */
  initialInstructions?: string;
}

/**
 * The empty-document affordance: describe the document and watch the AI stream it into
 * existence. Streaming renders into a local preview only — the viewport is never moved,
 * and the real body arrives through the ordinary query invalidation once applied.
 */
export function ComposeCard({ documentId, initialInstructions }: ComposeCardProps) {
  const { t } = useTranslation();
  const api = useApi();
  const queryClient = useQueryClient();

  const [instructions, setInstructions] = useState(initialInstructions ?? "");
  const [streaming, setStreaming] = useState(false);
  const [preview, setPreview] = useState("");
  const [error, setError] = useState<string | null>(null);
  // The stream appends fast; accumulating through state alone drops chunks under batching.
  const previewRef = useRef("");

  const start = async () => {
    setStreaming(true);
    setError(null);
    previewRef.current = "";
    setPreview("");

    try {
      await api.documents.sculptStream(
        documentId,
        {
          op: "compose",
          instructions,
          idempotencyKey: crypto.randomUUID(),
        },
        (event: SculptStreamEvent) => {
          if (event.type === "delta" && event.text) {
            previewRef.current += event.text;
            setPreview(previewRef.current);
          } else if (event.type === "error") {
            setError(event.message ?? t("Something went wrong"));
          }
        },
      );
      await queryClient.invalidateQueries({ queryKey: DOCUMENT_ROOT });
    } catch (thrown) {
      setError(thrown instanceof Error ? thrown.message : t("Something went wrong"));
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="rounded-md border border-divider bg-bg-secondary p-4">
      <div className="flex items-center gap-2">
        <Sparkle size={16} className="text-accent" aria-hidden />
        <Text variant="small" className="font-medium text-text">
          {t("Describe this document and let AI draft it")}
        </Text>
      </div>

      <textarea
        value={instructions}
        onChange={(event) => setInstructions(event.target.value)}
        rows={3}
        disabled={streaming}
        placeholder={t("e.g. A runbook for rotating the API gateway certificates")}
        className="mt-3 w-full resize-y rounded-md border border-input-border bg-bg p-2 text-body text-text focus:border-input-border-focus"
      />

      {error ? (
        <Text variant="small" className="mt-2 block text-danger">
          {error}
        </Text>
      ) : null}

      {streaming || preview ? (
        <div
          aria-live="polite"
          className="mt-3 max-h-72 overflow-y-auto whitespace-pre-wrap rounded-md border border-divider bg-bg p-3 font-mono text-small text-text-secondary"
        >
          {preview || t("The AI is writing…")}
        </div>
      ) : null}

      <div className="mt-3 flex justify-end">
        <Button
          variant="primary"
          disabled={streaming || instructions.trim().length === 0}
          onClick={() => void start()}
        >
          {streaming ? t("Drafting…") : t("Draft with AI")}
        </Button>
      </div>
    </div>
  );
}
